import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = 'https://api.viralgpt.com';

const Agent = () => {
  const L = useLanguage();
  const [tab, setTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [days, setDays] = useState(7);
  const [breakdownDays, setBreakdownDays] = useState(30);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (window.$crisp) window.$crisp.push(['do', 'chat:hide']);
    return () => { if (window.$crisp) window.$crisp.push(['do', 'chat:show']); };
  }, []);
  useEffect(() => { fetchSnapshot(); }, [days]);
  useEffect(() => { if (tab === 'data') fetchBreakdown(); }, [breakdownDays, tab]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const fetchSnapshot = async () => {
    try { const res = await axios.get(`${API}/agent/snapshot?days=${days}`); setSnapshot(res.data); } catch (e) { console.error(e); }
  };
  const fetchBreakdown = async () => {
    try { const res = await axios.get(`${API}/agent/breakdown?days=${breakdownDays}`); setBreakdown(res.data); } catch (e) { console.error(e); }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', content: text.trim() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/agent/chat`, { messages: updated, days });
      let content = res.data.message;
      if (res.data.toolLog?.length) {
        const logLines = res.data.toolLog.map(t => {
          if (t.tool === 'SQL') return `🔍 **SQL** — ${t.reason}`;
          if (t.tool === 'FETCH') return `🌐 **Fetch** — ${t.reason}`;
          if (t.tool === 'ADMIN') return `🔑 **Admin** — ${t.reason}`;
          if (t.tool === 'BROWSER') return `🖥️ **Browser** — ${t.reason}`;
          if (t.status === 'pending_confirmation') return `⏳ **Pending** — ${t.reason}`;
          return `⚙️ ${t.tool}${t.error ? ` — Error: ${t.error}` : ''}`;
        }).join('\n');
        content = `> **${L.agent.agentUsedTools(res.data.toolLog.length)}**\n${logLines}\n\n---\n\n${content}`;
      }
      const newMsgs = [...updated, { role: 'assistant', content }];
      if (res.data.pendingActions?.length) {
        newMsgs.push({ role: 'system', type: 'pending_actions', actions: res.data.pendingActions });
      }
      setMessages(newMsgs);
      if (res.data.snapshot) setSnapshot(res.data.snapshot);
    } catch (err) {
      setMessages([...updated, { role: 'assistant', content: `**${L.agent.errorLabel}** ${err.response?.data?.error || L.agent.requestFailed}` }]);
    } finally { setLoading(false); }
  };

  // Prompts are sent to the AI and intentionally stay in English; only labels are localized.
  const quickPrompts = [
    { label: L.agent.quickPrompts.fullFunnelAudit, prompt: 'Give me a complete funnel audit. Where is the biggest leak and what should I fix first?' },
    { label: L.agent.quickPrompts.annualVsMonthly, prompt: 'Analyze the annual vs monthly purchase split. How can we push more users to annual plans?' },
    { label: L.agent.quickPrompts.cpcRoas, prompt: 'Break down CPC, CPA, and ROAS. Are we spending efficiently? What should our targets be at this scale?' },
    { label: L.agent.quickPrompts.dailyTrends, prompt: 'Look at the daily data trends. Are there any anomalies or concerning patterns in the last few days?' },
    { label: L.agent.quickPrompts.scaleReadiness, prompt: 'Based on our current metrics, are we ready to scale ad spend? What benchmarks do we need to hit first?' },
    { label: L.agent.quickPrompts.revenueLeaks, prompt: 'Identify the top 3 revenue leaks in our funnel and quantify the potential impact of fixing each one.' },
    { label: L.agent.quickPrompts.activeSubscribers, prompt: 'Pull the active subscriber list and break down by plan type and interval. Who are our highest-value customers?' },
    { label: L.agent.quickPrompts.cohortAnalysis, prompt: 'Look at user cohort data. What is the retention rate month over month? How does LTV look across different acquisition months?' },
  ];

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', color: '#e4e4e7' }}>
      <div style={{ borderBottom: '1px solid #1e1e2e', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #635bff, #9854ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
            <i className="fa-solid fa-robot"></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '15px', color: '#fff' }}>{L.agent.headerTitle}</div>
            <div style={{ fontSize: '11px', color: '#71717a' }}>{L.agent.headerSubtitle}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={async () => {
            try {
              setLoading(true);
              await axios.post(`${API}/agent/send-daily-report`);
              alert(L.agent.reportSent);
            } catch (e) {
              alert(L.agent.reportFailed(e.response?.data?.error || e.message));
            } finally { setLoading(false); }
          }} style={{
            padding: '6px 14px', borderRadius: '8px', border: '1px solid #2e2e3e', cursor: 'pointer',
            fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px',
            background: '#141420', color: '#a1a1aa',
          }}>
            <i className="fa-brands fa-discord" style={{ fontSize: '12px', color: '#5865F2' }}></i>
            {L.agent.sendReport}
          </button>
          <div style={{ display: 'flex', gap: '4px', background: '#141420', borderRadius: '10px', padding: '3px', border: '1px solid #1e1e2e' }}>
            {[{ id: 'chat', icon: 'fa-robot', label: L.agent.tabAgent }, { id: 'data', icon: 'fa-chart-line', label: L.agent.tabData }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '6px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: '6px',
                background: tab === t.id ? '#635bff' : 'transparent', color: tab === t.id ? '#fff' : '#71717a',
              }}><i className={`fa-solid ${t.icon}`} style={{ fontSize: '10px' }}></i>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      {tab === 'chat' ? (
        <ChatTab
          messages={messages} setMessages={setMessages} input={input} setInput={setInput} loading={loading}
          sendMessage={sendMessage} quickPrompts={quickPrompts} chatEndRef={chatEndRef}
          days={days} setDays={setDays}
        />
      ) : (
        <DataTab breakdown={breakdown} breakdownDays={breakdownDays} setBreakdownDays={setBreakdownDays} />
      )}

      <style>{`
        @keyframes pulse { 0%,80%,100%{opacity:.3;transform:scale(.8)} 40%{opacity:1;transform:scale(1)} }
        .data-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .data-table th { position: sticky; top: 0; background: #0a0a0f; text-align: right; padding: 8px 10px; color: #71717a; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #1e1e2e; white-space: nowrap; }
        .data-table th:first-child { text-align: left; }
        .data-table td { text-align: right; padding: 7px 10px; border-bottom: 1px solid #111118; color: #a1a1aa; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .data-table td:first-child { text-align: left; color: #d4d4d8; font-weight: 500; }
        .data-table tr:hover td { background: #141420; }
        .green, .data-table td.green { color: #22c55e; }
        .yellow, .data-table td.yellow { color: #eab308; }
        .red, .data-table td.red { color: #ef4444; }
      `}</style>
    </div>
  );
};

const ChatTab = ({ messages, setMessages, input, setInput, loading, sendMessage, quickPrompts, chatEndRef, days, setDays }) => {
  const L = useLanguage();
  return (
  <div style={{ height: 'calc(100vh - 57px)', display: 'flex', flexDirection: 'column' }}>
    <div style={{ padding: '8px 24px', display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
      {[7, 14, 30].map(d => (
        <button key={d} onClick={() => setDays(d)} style={{
          padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
          fontSize: '11px', fontWeight: 600,
          background: days === d ? '#635bff' : '#1e1e2e', color: days === d ? '#fff' : '#71717a',
        }}>{L.agent.daysContext(d)}</button>
      ))}
    </div>
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>
      {messages.length === 0 && (
        <div style={{ textAlign: 'center', paddingTop: '60px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>{L.agent.readyTitle}</h2>
          <p style={{ fontSize: '14px', color: '#71717a', maxWidth: '480px', margin: '0 auto 32px', lineHeight: 1.6 }}>
            {L.agent.readyDesc}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', maxWidth: '600px', margin: '0 auto' }}>
            {quickPrompts.map((qp, i) => (
              <button key={i} onClick={() => sendMessage(qp.prompt)} style={{
                padding: '8px 16px', borderRadius: '10px', border: '1px solid #2e2e3e', background: '#141420',
                color: '#a1a1aa', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}>{qp.label}</button>
            ))}
          </div>
        </div>
      )}
      {messages.map((msg, i) => {
        if (msg.type === 'pending_actions') {
          return (
            <div key={i} style={{ marginBottom: '20px' }}>
              {msg.actions.map(action => (
                <PendingActionCard key={action.id} action={action} onResult={(result) => {
                  setMessages(prev => prev.map(m => m === msg ? { ...m, actions: m.actions.map(a => a.id === action.id ? { ...a, resolved: result } : a) } : m));
                }} />
              ))}
            </div>
          );
        }
        return (
          <div key={i} style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0, background: msg.role === 'user' ? '#1e1e2e' : 'linear-gradient(135deg, #635bff, #9854ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#fff' }}>
              <i className={msg.role === 'user' ? 'fa-solid fa-user' : 'fa-solid fa-robot'}></i>
            </div>
            <div style={{ maxWidth: '80%', padding: '12px 16px', borderRadius: '14px', fontSize: '14px', lineHeight: 1.7, background: msg.role === 'user' ? '#635bff' : '#141420', color: msg.role === 'user' ? '#fff' : '#d4d4d8', border: msg.role === 'user' ? 'none' : '1px solid #1e1e2e', whiteSpace: 'pre-wrap' }}>
              {msg.role === 'assistant' ? <MarkdownLite text={msg.content} /> : msg.content}
            </div>
          </div>
        );
      })}
      {loading && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'linear-gradient(135deg, #635bff, #9854ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#fff' }}><i className="fa-solid fa-robot"></i></div>
          <div style={{ padding: '12px 16px', borderRadius: '14px', background: '#141420', border: '1px solid #1e1e2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '4px' }}>{[0,1,2].map(i => <div key={i} style={{ width:6,height:6,borderRadius:'50%',background:'#635bff',animation:'pulse 1.4s infinite',animationDelay:`${i*.2}s` }}/>)}</div>
            <span style={{ fontSize: '12px', color: '#71717a' }}>{L.agent.analyzing}</span>
          </div>
        </div>
      )}
      <div ref={chatEndRef} />
    </div>
    <div style={{ padding: '12px 24px', borderTop: '1px solid #1e1e2e' }}>
      <div style={{ display: 'flex', gap: '8px', maxWidth: '800px', margin: '0 auto' }}>
        <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !loading && sendMessage(input)}
          placeholder={L.agent.inputPlaceholder} style={{ flex: 1, padding: '12px 16px', borderRadius: '12px', border: '1px solid #2e2e3e', background: '#141420', color: '#fff', fontSize: '14px', outline: 'none' }} />
        <button onClick={() => sendMessage(input)} disabled={loading || !input.trim()} style={{ padding: '12px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: '#635bff', color: '#fff', fontWeight: 600, fontSize: '14px', opacity: loading || !input.trim() ? 0.5 : 1 }}>
          <i className="fa-solid fa-paper-plane"></i>
        </button>
      </div>
    </div>
  </div>
  );
};

const rc = (val, good, great, invert) => {
  if (val == null) return '';
  if (invert) return val <= great ? 'green' : val <= good ? 'yellow' : 'red';
  return val >= great ? 'green' : val >= good ? 'yellow' : 'red';
};

const $ = v => v != null ? `$${Number(v).toFixed(0)}` : '-';
const pct = v => v != null ? `${v}%` : '-';

const SECTIONS = {
  overview: { label: 'Overview', icon: 'fa-chart-pie' },
  funnel: { label: 'Funnel', icon: 'fa-filter' },
  revenue: { label: 'Revenue', icon: 'fa-dollar-sign' },
  costs: { label: 'Costs & P/L', icon: 'fa-receipt' },
  performance: { label: 'Performance', icon: 'fa-gauge-high' },
};

const DataTab = ({ breakdown, breakdownDays, setBreakdownDays }) => {
  const L = useLanguage();
  const [view, setView] = useState('daily');
  const [section, setSection] = useState('overview');

  if (!breakdown) return <div style={{ padding: '60px', textAlign: 'center', color: '#71717a' }}>{L.agent.loadingData}</div>;

  const rows = [...(view === 'daily' ? breakdown.daily : view === 'weekly' ? breakdown.weekly : breakdown.monthly)].reverse();
  const t = breakdown.totals;
  const dateLabel = L.agent.dateLabels[view];

  const annPct = (r) => { const tot = (r.annual||0)+(r.monthly||0); return tot > 0 ? +((r.annual/tot)*100).toFixed(0) : null; };
  const margin = (r) => r.grossRevenue > 0 ? +((r.netProfit/r.grossRevenue)*100).toFixed(0) : null;

  const sections = {
    overview: {
      cards: [
        { label: L.agent.cardLabels.adSpend, value: $(t.spend) },
        { label: L.agent.cardLabels.grossRevenue, value: $(t.grossRevenue), color: '#818cf8' },
        { label: L.agent.cardLabels.netProfit, value: $(t.netProfit), color: t.netProfit >= 0 ? '#22c55e' : '#ef4444' },
        { label: L.agent.cardLabels.roas, value: `${t.roas}x`, color: t.roas >= 2 ? '#22c55e' : t.roas >= 1 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.cpa, value: $(t.cpa), color: t.cpa <= 50 ? '#22c55e' : t.cpa <= 80 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.newSubs, value: t.newSubs },
        { label: L.agent.cardLabels.lpToPurchase, value: pct(t.lpToPurchase), color: t.lpToPurchase >= 3 ? '#22c55e' : t.lpToPurchase >= 1 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.annualPct, value: pct(t.annualRatio), color: t.annualRatio >= 60 ? '#22c55e' : t.annualRatio >= 40 ? '#eab308' : '#ef4444' },
      ],
      headers: L.agent.tableHeaders.overview,
      row: r => [
        { v: $(r.spend) },
        { v: r.views?.toLocaleString() },
        { v: r.purchase, bold: true },
        { v: $(r.grossRevenue), c: '#818cf8' },
        { v: $(r.netProfit), cls: r.netProfit >= 0 ? 'green' : 'red', bold: true },
        { v: r.roas != null ? `${r.roas}x` : '-', cls: rc(r.roas, 1, 2) },
        { v: r.cpa != null ? $(r.cpa) : '-', cls: rc(r.cpa, 80, 50, true) },
        { v: r.cpc != null ? `$${r.cpc}` : '-', cls: rc(r.cpc, 2, 1, true) },
        { v: pct(r.lpToPurchase), cls: rc(r.lpToPurchase, 1, 3) },
        { v: pct(annPct(r)), cls: rc(annPct(r), 40, 60) },
      ],
    },
    funnel: {
      cards: [
        { label: L.agent.cardLabels.views, value: t.views?.toLocaleString() },
        { label: L.agent.cardLabels.addToCart, value: t.atc },
        { label: L.agent.cardLabels.registrations, value: t.registrations },
        { label: L.agent.cardLabels.checkouts, value: t.checkouts },
        { label: L.agent.cardLabels.purchases, value: t.purchase },
        { label: L.agent.cardLabels.lpToAtc, value: pct(t.lpToAtc), color: t.lpToAtc >= 15 ? '#22c55e' : t.lpToAtc >= 8 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.atcToReg, value: pct(t.atcToReg), color: t.atcToReg >= 50 ? '#22c55e' : t.atcToReg >= 30 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.regToChk, value: pct(t.regToCheckout), color: t.regToCheckout >= 60 ? '#22c55e' : t.regToCheckout >= 40 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.chkToPur, value: pct(t.checkoutToPurchase), color: t.checkoutToPurchase >= 40 ? '#22c55e' : t.checkoutToPurchase >= 25 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.lpToPur, value: pct(t.lpToPurchase), color: t.lpToPurchase >= 3 ? '#22c55e' : t.lpToPurchase >= 1 ? '#eab308' : '#ef4444' },
      ],
      headers: L.agent.tableHeaders.funnel,
      row: r => [
        { v: r.views?.toLocaleString() },
        { v: r.atc },
        { v: r.reg },
        { v: r.checkout },
        { v: r.purchase, bold: true },
        { v: pct(r.lpToAtc), cls: rc(r.lpToAtc, 8, 15) },
        { v: pct(r.atcToReg), cls: rc(r.atcToReg, 30, 50) },
        { v: pct(r.regToCheckout), cls: rc(r.regToCheckout, 40, 60) },
        { v: pct(r.checkoutToPurchase), cls: rc(r.checkoutToPurchase, 25, 40) },
        { v: pct(r.lpToPurchase), cls: rc(r.lpToPurchase, 1, 3) },
      ],
    },
    revenue: {
      cards: [
        { label: L.agent.cardLabels.initialRevenue, value: $(t.revenue), color: '#22c55e' },
        { label: L.agent.cardLabels.recurringRevenue, value: $(t.recurring), color: '#3b82f6' },
        { label: L.agent.cardLabels.topupRevenue, value: $(t.topupRevenue), color: '#a855f7' },
        { label: L.agent.cardLabels.grossRevenue, value: $(t.grossRevenue), color: '#818cf8' },
        { label: L.agent.cardLabels.newSubs, value: t.newSubs },
        { label: L.agent.cardLabels.annualPurchases, value: t.annual },
        { label: L.agent.cardLabels.monthlyPurchases, value: t.monthly },
        { label: L.agent.cardLabels.annualRatio, value: pct(t.annualRatio), color: t.annualRatio >= 60 ? '#22c55e' : t.annualRatio >= 40 ? '#eab308' : '#ef4444' },
      ],
      headers: L.agent.tableHeaders.revenue,
      row: r => [
        { v: r.newSubs || 0, bold: true },
        { v: $(r.revenue), c: '#22c55e' },
        { v: $(r.recurring), c: '#3b82f6' },
        { v: $((r.topupRevenue || 0)), c: '#a855f7' },
        { v: $(r.grossRevenue), c: '#818cf8' },
        { v: r.annual || 0, cls: rc(annPct(r), 40, 60) },
        { v: r.monthly || 0 },
        { v: pct(annPct(r)), cls: rc(annPct(r), 40, 60) },
        { v: r.roas != null ? `${r.roas}x` : '-', cls: rc(r.roas, 1, 2) },
      ],
    },
    costs: {
      cards: [
        { label: L.agent.cardLabels.adSpend, value: $(t.spend), color: '#ef4444' },
        { label: L.agent.cardLabels.subCosts, value: $(t.subCosts) },
        { label: L.agent.cardLabels.productCosts, value: $(t.productCosts) },
        { label: L.agent.cardLabels.fees, value: $(t.fees) },
        { label: L.agent.cardLabels.refunds, value: $(t.refunds) },
        { label: L.agent.cardLabels.chargebacks, value: $(t.chargebacks) },
        { label: L.agent.cardLabels.grossRevenue, value: $(t.grossRevenue), color: '#818cf8' },
        { label: L.agent.cardLabels.netProfit, value: $(t.netProfit), color: t.netProfit >= 0 ? '#22c55e' : '#ef4444' },
        { label: L.agent.cardLabels.margin, value: t.grossRevenue > 0 ? pct(+((t.netProfit / t.grossRevenue) * 100).toFixed(0)) : '-', color: t.netProfit >= 0 ? '#22c55e' : '#ef4444' },
      ],
      headers: L.agent.tableHeaders.costs,
      row: r => [
        { v: $(r.spend), c: '#ef4444' },
        { v: $(r.grossRevenue), c: '#818cf8' },
        { v: $((r.subCosts || 0)) },
        { v: $((r.productCosts || 0)) },
        { v: $((r.fees || 0)) },
        { v: $((r.refunds || 0)) },
        { v: $((r.chargebacks || 0)) },
        { v: $(r.netProfit), cls: r.netProfit >= 0 ? 'green' : 'red', bold: true },
        { v: pct(margin(r)), cls: margin(r) != null ? (margin(r) >= 20 ? 'green' : margin(r) >= 0 ? 'yellow' : 'red') : '' },
      ],
    },
    performance: {
      cards: [
        { label: L.agent.cardLabels.cpc, value: t.cpc != null ? `$${t.cpc}` : '-', color: t.cpc <= 1 ? '#22c55e' : t.cpc <= 2 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.cpa, value: $(t.cpa), color: t.cpa <= 50 ? '#22c55e' : t.cpa <= 80 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.roas, value: `${t.roas}x`, color: t.roas >= 2 ? '#22c55e' : t.roas >= 1 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.lpToPurchase, value: pct(t.lpToPurchase), color: t.lpToPurchase >= 3 ? '#22c55e' : t.lpToPurchase >= 1 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.lpToAtc, value: pct(t.lpToAtc), color: t.lpToAtc >= 15 ? '#22c55e' : t.lpToAtc >= 8 ? '#eab308' : '#ef4444' },
        { label: L.agent.cardLabels.chkToPur, value: pct(t.checkoutToPurchase), color: t.checkoutToPurchase >= 40 ? '#22c55e' : t.checkoutToPurchase >= 25 ? '#eab308' : '#ef4444' },
      ],
      headers: L.agent.tableHeaders.performance,
      row: r => [
        { v: $(r.spend) },
        { v: r.views?.toLocaleString() },
        { v: r.purchase, bold: true },
        { v: r.cpc != null ? `$${r.cpc}` : '-', cls: rc(r.cpc, 2, 1, true) },
        { v: r.cpa != null ? $(r.cpa) : '-', cls: rc(r.cpa, 80, 50, true) },
        { v: r.roas != null ? `${r.roas}x` : '-', cls: rc(r.roas, 1, 2) },
        { v: pct(r.lpToAtc), cls: rc(r.lpToAtc, 8, 15) },
        { v: pct(r.checkoutToPurchase), cls: rc(r.checkoutToPurchase, 25, 40) },
        { v: pct(r.lpToPurchase), cls: rc(r.lpToPurchase, 1, 3) },
      ],
    },
  };

  const sec = sections[section];

  return (
    <div style={{ height: 'calc(100vh - 57px)', display: 'flex', flexDirection: 'column' }}>
      {/* Controls */}
      <div style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', borderBottom: '1px solid #1e1e2e' }}>
        <div style={{ display: 'flex', gap: '3px', background: '#0f0f18', borderRadius: '10px', padding: '3px', border: '1px solid #1e1e2e' }}>
          {Object.entries(SECTIONS).map(([id, s]) => (
            <button key={id} onClick={() => setSection(id)} style={{
              padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
              fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px',
              background: section === id ? '#635bff' : 'transparent', color: section === id ? '#fff' : '#71717a',
            }}><i className={`fa-solid ${s.icon}`} style={{ fontSize: '9px' }}></i>{L.agent.sections[id]}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '3px' }}>
            {['daily', 'weekly', 'monthly'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontSize: '11px', fontWeight: 600, textTransform: 'capitalize',
                background: view === v ? '#1e1e2e' : 'transparent', color: view === v ? '#fff' : '#52525b',
              }}>{L.agent.viewLabels[v]}</button>
            ))}
          </div>
          <div style={{ width: '1px', height: '16px', background: '#1e1e2e' }}></div>
          <div style={{ display: 'flex', gap: '3px' }}>
            {[7, 14, 30, 60, 90].map(d => (
              <button key={d} onClick={() => setBreakdownDays(d)} style={{
                padding: '4px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontSize: '10px', fontWeight: 600,
                background: breakdownDays === d ? '#1e1e2e' : 'transparent', color: breakdownDays === d ? '#fff' : '#52525b',
              }}>{d}D</button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ padding: '12px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '6px' }}>
        {sec.cards.map((c, i) => <SummaryCard key={i} label={c.label} value={c.value} color={c.color} />)}
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '0 24px 24px' }}>
        <table className="data-table">
          <thead><tr><th style={{ textAlign: 'left' }}>{dateLabel}</th>{sec.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const label = r.date || r.week || r.month;
              const cells = sec.row(r);
              return (
                <tr key={i}>
                  <td>{label}</td>
                  {cells.map((cell, j) => (
                    <td key={j} className={cell.cls || ''} style={{ fontWeight: cell.bold ? 700 : 400, color: cell.c || undefined }}>{cell.v}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ padding: '6px 24px 10px', display: 'flex', gap: '16px', fontSize: '10px', borderTop: '1px solid #1e1e2e' }}>
        <span><span className="green" style={{ marginRight: '4px' }}>●</span>{L.agent.legendExceeds}</span>
        <span><span className="yellow" style={{ marginRight: '4px' }}>●</span>{L.agent.legendMeets}</span>
        <span><span className="red" style={{ marginRight: '4px' }}>●</span>{L.agent.legendBelow}</span>
      </div>
    </div>
  );
};

const PendingActionCard = ({ action, onResult }) => {
  const L = useLanguage();
  const [status, setStatus] = useState(action.resolved || null);
  const [loading, setLoading] = useState(false);

  const handle = async (approve) => {
    setLoading(true);
    try {
      const endpoint = approve ? 'confirm' : 'reject';
      const res = await axios.post(`${API}/agent/${endpoint}/${action.id}`);
      const result = approve ? L.agent.approvedResult(res.data.result) : L.agent.rejected;
      setStatus(result);
      onResult(result);
    } catch (e) {
      setStatus(L.agent.errorResult(e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  return (
    <div style={{ background: '#1a1520', border: '1px solid #2e2e3e', borderRadius: '12px', padding: '14px 16px', marginBottom: '8px', maxWidth: '500px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: status ? '#1e1e2e' : '#f59e0b22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={status ? 'fa-solid fa-check' : 'fa-solid fa-shield-halved'} style={{ fontSize: '10px', color: status ? '#71717a' : '#f59e0b' }}></i>
        </div>
        <span style={{ fontSize: '11px', fontWeight: 700, color: status ? '#71717a' : '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {status ? L.agent.resolved : L.agent.requiresApproval}
        </span>
      </div>
      <div style={{ fontSize: '13px', color: '#d4d4d8', marginBottom: status ? '0' : '12px' }}>
        <strong style={{ color: '#fff' }}>{action.tool}</strong>: {action.description}
      </div>
      {status ? (
        <div style={{ fontSize: '12px', color: '#71717a', marginTop: '6px' }}>{status}</div>
      ) : (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => handle(true)} disabled={loading} style={{
            padding: '6px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            background: '#22c55e', color: '#fff', fontSize: '12px', fontWeight: 600, opacity: loading ? 0.5 : 1,
          }}>{L.agent.approve}</button>
          <button onClick={() => handle(false)} disabled={loading} style={{
            padding: '6px 16px', borderRadius: '8px', border: '1px solid #2e2e3e', cursor: 'pointer',
            background: 'transparent', color: '#ef4444', fontSize: '12px', fontWeight: 600, opacity: loading ? 0.5 : 1,
          }}>{L.agent.reject}</button>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div style={{ background: '#141420', borderRadius: '10px', padding: '10px 14px', border: '1px solid #1e1e2e' }}>
    <div style={{ fontSize: '10px', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '20px', fontWeight: 700, color: color || '#fff', letterSpacing: '-0.5px' }}>{value}</div>
  </div>
);

const MarkdownLite = ({ text }) => {
  const lines = text.split('\n');
  return (
    <div>
      {lines.map((line, i) => {
        if (line.startsWith('### ')) return <h4 key={i} style={{ fontSize: '15px', fontWeight: 700, color: '#fff', margin: '16px 0 6px' }}>{line.slice(4)}</h4>;
        if (line.startsWith('## ')) return <h3 key={i} style={{ fontSize: '17px', fontWeight: 700, color: '#fff', margin: '20px 0 8px' }}>{line.slice(3)}</h3>;
        if (line.startsWith('# ')) return <h2 key={i} style={{ fontSize: '20px', fontWeight: 700, color: '#fff', margin: '20px 0 8px' }}>{line.slice(2)}</h2>;
        if (line.startsWith('- **') || line.startsWith('* **')) return <div key={i} style={{ paddingLeft: '12px', margin: '4px 0', borderLeft: '2px solid #2e2e3e' }}>{parseBold(line.slice(2))}</div>;
        if (line.startsWith('- ') || line.startsWith('* ')) return <div key={i} style={{ paddingLeft: '12px', margin: '3px 0' }}>• {parseBold(line.slice(2))}</div>;
        if (line.startsWith('> ')) return <div key={i} style={{ borderLeft: '3px solid #635bff', paddingLeft: '12px', color: '#a1a1aa', margin: '8px 0', fontStyle: 'italic' }}>{parseBold(line.slice(2))}</div>;
        if (line.trim() === '---') return <hr key={i} style={{ border: 'none', borderTop: '1px solid #1e1e2e', margin: '12px 0' }} />;
        if (line.trim() === '') return <div key={i} style={{ height: '8px' }} />;
        return <div key={i} style={{ margin: '3px 0' }}>{parseBold(line)}</div>;
      })}
    </div>
  );
};

function parseBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} style={{ color: '#fff', fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
    return part;
  });
}

export default Agent;
